import { useCreateBriefDataFields } from "@entities/brief/lib/hooks/useCreateBriefDataFields";
import { useDeleteBriefDataField } from "@entities/brief/lib/hooks/useDeleteBriefDataField";
import { useGetBriefDataFields } from "@entities/brief/lib/hooks/useGetBriefDataFields";
import { useToggleBriefDataFieldV2 } from "@entities/brief/lib/hooks/useToggleBriefDataFieldV2";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import {
  CustomFieldType,
  IFieldConfig,
} from "@shared/lib/types/briefInfluencerV2";
import { useEffect, useMemo, useState } from "react";
import { useBriefCustomFieldsForm } from "./useBriefCustomFieldsForm";

export type HubFieldScope = "internal" | "brand";

export function useManageColumnsHub(onClose?: () => void) {
  const { briefId, brief } = useBriefStore();
  const [openCreateScope, setOpenCreateScope] = useState<HubFieldScope | null>(
    null
  );
  const [expandedFieldKey, setExpandedFieldKey] = useState<string | null>(null);
  const [createScope, setCreateScope] = useState<HubFieldScope>("internal");

  const [visiblePerformanceFields, setVisiblePerformanceFields] = useState<
    IFieldConfig[]
  >([]);
  const [visibleInternalFields, setVisibleInternalFields] = useState<
    IFieldConfig[]
  >([]);
  const [visibleBrandFields, setVisibleBrandFields] = useState<IFieldConfig[]>(
    []
  );
  const [visibleBriefTableColumns, setVisibleBriefTableColumns] = useState<string[]>([]);

  const { dataFields, isFetching } = useGetBriefDataFields(briefId);

  const performanceFieldOptions = dataFields?.performanceFields || [];
  const internalFieldOptions =
    dataFields?.customFields.filter((field) => !field.isShared) || [];
  const brandFieldOptions =
    dataFields?.customFields.filter((field) => field.isShared) || [];

  useEffect(() => {
    setVisiblePerformanceFields(
      brief?.dataFieldsV2?.filter((field) => !field.isSpecialField) || []
    );
    setVisibleInternalFields(
      brief?.dataFieldsV2?.filter(
        (field) => field.isSpecialField && !field.isShared
      ) || []
    );
    setVisibleBrandFields(brief?.visibleBrandCustomFieldsV2 || []);
    setVisibleBriefTableColumns(brief?.visibleBriefTableColumns || []);
  }, [brief?.dataFieldsV2, brief?.visibleBrandCustomFieldsV2, brief?.visibleBriefTableColumns]);

  const { createDataFields, isCreating, isCreated } = useCreateBriefDataFields(
    briefId || ""
  );
  const { toggleDataField, isToggling, isToggled } = useToggleBriefDataFieldV2(
    briefId || ""
  );
  const { deleteDataField, isDeleting, isDeleted, deleteDataFieldVariables } =
    useDeleteBriefDataField(briefId || "");

  const {
    fieldName,
    fieldType,
    fieldDefault,
    dropdownOptions,
    setFieldName,
    setFieldType,
    setFieldDefault,
    setDropdownOptions,
    handleAddOption,
    handleOptionChange,
    handleRemoveOption,
    resetForm,
  } = useBriefCustomFieldsForm({
    selectedField: null,
    isEditMode: false,
  });

  useEffect(() => {
    if (isCreated) {
      ToastService.success("Field created successfully");
      setOpenCreateScope(null);
      resetForm();
    }
  }, [isCreated]);

  useEffect(() => {
    if (isDeleted) {
      ToastService.success("Field deleted successfully");
      setExpandedFieldKey(null);
    }
  }, [isDeleted]);

  useEffect(() => {
    if(isToggled) {
      ToastService.success("Column settings updated successfully");
      onClose?.();
    }
  }, [isToggled]);

  const visiblePerformanceCount = visiblePerformanceFields.length;
  const visibleInternalCount = useMemo(
    () =>
      internalFieldOptions.filter((field) =>
        visibleInternalFields.some((visible) => visible.key === field.key)
      ).length,
    [internalFieldOptions, visibleInternalFields]
  );
  const visibleBrandCount = useMemo(
    () =>
      brandFieldOptions.filter((field) =>
        visibleBrandFields.some((visible) => visible.key === field.key)
      ).length,
    [brandFieldOptions, visibleBrandFields]
  );

  const isPerformanceVisible = (field: IFieldConfig) =>
    visiblePerformanceFields.some((visible) => visible.key === field.key);

  const isInternalVisible = (field: IFieldConfig) =>
    visibleInternalFields.some((visible) => visible.key === field.key);

  const isBrandVisible = (field: IFieldConfig) =>
    visibleBrandFields.some((visible) => visible.key === field.key);

  const togglePerformanceVisibility = (field: IFieldConfig) => {
    setVisiblePerformanceFields((current) =>
      isPerformanceVisible(field)
        ? current.filter((item) => item.key !== field.key)
        : [...current, field]
    );
  };

  const togglePerformanceCategoryVisibility = (fields: IFieldConfig[]) => {
    const allVisible = fields.every((field) => isPerformanceVisible(field));

    setVisiblePerformanceFields((current) => {
      if (allVisible) {
        return current.filter(
          (item) => !fields.some((field) => field.key === item.key)
        );
      }

      const next = [...current];
      fields.forEach((field) => {
        if (!next.some((item) => item.key === field.key)) {
          next.push(field);
        }
      });
      return next;
    });
  };

  const toggleInternalVisibility = (field: IFieldConfig) => {
    setVisibleInternalFields((current) =>
      isInternalVisible(field)
        ? current.filter((item) => item.key !== field.key)
        : [...current, field]
    );
  };

  const toggleBrandVisibility = (field: IFieldConfig) => {
    setVisibleBrandFields((current) =>
      isBrandVisible(field)
        ? current.filter((item) => item.key !== field.key)
        : [...current, field]
    );
  };

  const openCreateForm = (scope: HubFieldScope) => {
    setExpandedFieldKey(null);
    setCreateScope(scope);
    setOpenCreateScope((current) => {
      resetForm();
      if (current === scope) {
        return null;
      }
      return scope;
    });
  };

  const toggleFieldView = (field: IFieldConfig) => {
    setOpenCreateScope(null);
    setExpandedFieldKey((current) =>
      current === field.key ? null : field.key
    );
  };

  const closeCreateForm = () => {
    setOpenCreateScope(null);
    resetForm();
  };

  const closeFieldView = () => {
    setExpandedFieldKey(null);
  };

  const handleCreateSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();

    if (!openCreateScope) {
      return;
    }

    if (!fieldName.trim()) {
      ToastService.error("Field name is required");
      return;
    }

    if (fieldType === CustomFieldType.DROPDOWN) {
      const validOptions = dropdownOptions.filter((option) => option.trim());
      if (validOptions.length === 0) {
        ToastService.error("At least one dropdown option is required");
        return;
      }
    }

    if (!briefId) {
      ToastService.error("Brief ID is required");
      return;
    }

    const fieldConfig: IFieldConfig = {
      key: `${fieldName.toLowerCase().replace(/\s+/g, "_")}_${Date.now()}`,
      isSpecialField: true,
      type: fieldType,
      displayName: fieldName,
      defaultValue: fieldDefault,
      dropdownOptions:
        fieldType === CustomFieldType.DROPDOWN
          ? dropdownOptions.filter((option) => option.trim())
          : undefined,
      isShared: createScope === "brand",
    };

    createDataFields({
      briefId,
      customFieldsV2: fieldConfig,
    });
  };

  const isBriefTableColumnVisible = (key: string) =>
    visibleBriefTableColumns.includes(key);

  const toggleBriefTableColumnVisibility = (key: string) => {
    setVisibleBriefTableColumns((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    );
  };

  const handleApplyChanges = () => {
    if (!briefId || !brief?.brandId) {
      ToastService.error("Brief ID and Brand ID are required");
      return;
    }

    toggleDataField({
      briefId,
      brandId: brief.brandId,
      visibleBrandCustomFieldsV2: visibleBrandFields,
      visibleBriefTableColumns: visibleBriefTableColumns,
      // visibleBrandPerformanceFieldsV2: visiblePerformanceFields,
      dataFieldsV2: [...visibleInternalFields, ...visiblePerformanceFields],
    });
  };

  const handleDeleteField = (field: IFieldConfig) => {
    if (!briefId || !field._id) {
      ToastService.error("Field ID is required");
      return;
    }

    deleteDataField({
      briefId,
      customFieldId: field._id,
    });
  };

  const isFieldDeleting = (fieldId?: string) =>
    Boolean(isDeleting && deleteDataFieldVariables?.customFieldId === fieldId);

  return {
    openCreateScope,
    expandedFieldKey,
    createScope,
    isFetching,
    isCreating,
    isToggling,
    performanceFieldOptions,
    internalFieldOptions,
    brandFieldOptions,
    visiblePerformanceCount,
    visibleInternalCount,
    visibleBrandCount,
    fieldName,
    fieldType,
    fieldDefault,
    dropdownOptions,
    setFieldName,
    setFieldType,
    setFieldDefault,
    setDropdownOptions,
    handleAddOption,
    handleOptionChange,
    handleRemoveOption,
    isPerformanceVisible,
    isInternalVisible,
    isBrandVisible,
    togglePerformanceVisibility,
    togglePerformanceCategoryVisibility,
    toggleInternalVisibility,
    toggleBrandVisibility,
    openCreateForm,
    toggleFieldView,
    closeCreateForm,
    closeFieldView,
    handleCreateSubmit,
    handleApplyChanges,
    handleDeleteField,
    isFieldDeleting,
    visibleBriefTableColumns,
    isBriefTableColumnVisible,
    toggleBriefTableColumnVisibility,
  };
}
